import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { API } from "../api";
import { Loader2, Download, CheckCircle2, Ticket, CalendarDays, MapPin, Smartphone, Share2, User } from "lucide-react";
import { AddToCalendar, Countdown } from "../components/EventExtras";
import { formatSeat } from "../components/SeatMap";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

const statusMap = {
  valid: { label: "Valid", color: "#16a34a", bg: "#dcfce7" },
  checked_in: { label: "Checked in", color: "#d97706", bg: "#fef3c7" },
  cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" },
  pending: { label: "Awaiting payment", color: "#6b7280", bg: "#f3f4f6" },
};

export default function VoucherPage() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const isNew = sp.get("new") === "1";
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showTip, setShowTip] = useState(false);

  const saveToPhone = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "My event ticket", text: "My event ticket", url }); return; } catch { /* cancelled */ }
    }
    setShowTip((s) => !s);
  };

  useEffect(() => {
    api.get(`/voucher/${code}`).then(({ data }) => setV(data)).catch(() => setError("Ticket not found")).finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-[#5a6b80] text-lg">{error}</p>
      <Link to="/check-balance" className="gp-btn-teal rounded-full px-6 py-2.5 font-medium">Check another code</Link>
    </div>
  );

  const st = statusMap[v.status] || statusMap.valid;
  const brand = v.brand_color || "#16243b";

  return (
    <div className="min-h-screen bg-[#f6f8fb] py-12 px-4">
      <div className="max-w-[480px] mx-auto">
        {isNew && (
          <div className="bg-[#dcfce7] text-[#166534] rounded-xl p-4 mb-6 flex items-center gap-2">
            <CheckCircle2 size={20} /> <span className="font-medium">Booking confirmed! Your ticket is ready.</span>
          </div>
        )}
        <div className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(20,30,55,0.12)]">
          <div className="p-8 text-white" style={{ background: brand }}>
            <div className="flex items-center gap-2 opacity-90"><Ticket size={20} /><span className="text-sm">E-Ticket</span></div>
            <h1 className="text-2xl font-bold mt-3">{v.product_title}</h1>
            <p className="opacity-85 mt-1">{v.business_name}</p>
          </div>
          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[#5a6b80] text-sm">Ticket status</span>
              <span className="text-xs font-semibold px-3 py-1 rounded-full" data-testid="ticket-status" style={{ color: st.color, background: st.bg }}>{st.label}</span>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-2 text-[#16243b]"><User size={17} className="text-[#2ec9c9] mt-0.5" /><div><p className="text-xs text-[#8a97a8]">Attendee</p><p className="font-semibold">{v.attendee_name || "—"}</p></div></div>
              <div className="flex items-start gap-2 text-[#16243b]"><Ticket size={17} className="text-[#2ec9c9] mt-0.5" /><div><p className="text-xs text-[#8a97a8]">Ticket type</p><p className="font-semibold">{v.tier_name} · £{(v.price || 0).toFixed(2)}</p></div></div>
              {v.seat && (
                <div className="flex items-start gap-2 text-[#16243b]" data-testid="voucher-seat"><Ticket size={17} className="text-[#2ec9c9] mt-0.5 opacity-0" /><div><p className="text-xs text-[#8a97a8]">Seat</p><p className="font-semibold">{formatSeat(v.seat)}</p></div></div>
              )}
              {v.booking_fee > 0 && (
                <div className="flex items-start gap-2 text-[#16243b]" data-testid="voucher-booking-fee"><Ticket size={17} className="text-[#2ec9c9] mt-0.5 opacity-0" /><div><p className="text-xs text-[#8a97a8]">Booking fee</p><p className="font-semibold">£{v.booking_fee.toFixed(2)}</p></div></div>
              )}
              {v.event_date && <div className="flex items-start gap-2 text-[#16243b]"><CalendarDays size={17} className="text-[#2ec9c9] mt-0.5" /><div><p className="text-xs text-[#8a97a8]">Date &amp; time</p><p className="font-semibold">{fmtDate(v.event_date)}</p></div></div>}
              {v.venue_name && <div className="flex items-start gap-2 text-[#16243b]"><MapPin size={17} className="text-[#2ec9c9] mt-0.5" /><div><p className="text-xs text-[#8a97a8]">Venue</p><p className="font-semibold">{v.venue_name}{v.venue_address ? `, ${v.venue_address}` : ""}</p></div></div>}
            </div>

            {v.event_date && (
              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="ticket-event-extras">
                <Countdown iso={v.event_date} />
                <AddToCalendar event={{ title: v.product_title, iso: v.event_date, location: [v.venue_name, v.venue_address].filter(Boolean).join(", "), details: `Your ${v.tier_name} ticket — code ${v.code}` }} />
              </div>
            )}

            {v.checked_in && (
              <div className="mt-4 bg-amber-50 border border-amber-100 text-amber-700 rounded-xl p-3 text-sm">
                This ticket has already been checked in{v.checked_in_at ? ` at ${new Date(v.checked_in_at).toLocaleString("en-GB")}` : ""}.
              </div>
            )}

            <div className="mt-6 bg-[#f6f8fb] rounded-xl p-4 flex items-center gap-4">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=0&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : v.code)}`}
                alt="Ticket QR code" width="88" height="88" className="rounded-lg bg-white p-1 shrink-0" data-testid="voucher-qr" />
              <div>
                <p className="text-xs text-[#8a97a8] mb-1">Ticket code</p>
                <p className="font-mono font-bold text-lg tracking-wider text-[#16243b]">{v.code}</p>
                <p className="text-xs text-[#8a97a8] mt-1">Show this QR at the door to check in.</p>
              </div>
            </div>

            <button onClick={saveToPhone} data-testid="save-to-phone-btn"
              className="w-full rounded-xl py-3 font-medium mt-4 flex items-center justify-center gap-2 border-2 border-[#16243b] text-[#16243b] hover:bg-[#16243b] hover:text-white transition-colors">
              {navigator.share ? <Share2 size={18} /> : <Smartphone size={18} />} Save to phone
            </button>
            {showTip && (
              <div className="mt-3 bg-[#eef7ff] border border-[#d6e8fb] text-[#2a4a6b] rounded-xl p-3 text-sm" data-testid="save-to-phone-tip">
                <p className="font-medium mb-1">Keep this ticket on your phone:</p>
                <p><strong>iPhone:</strong> tap the Share icon in Safari → <em>Add to Home Screen</em>.</p>
                <p><strong>Android:</strong> tap the ⋮ menu in Chrome → <em>Add to Home screen</em>.</p>
              </div>
            )}

            <a href={`${API}/voucher/${v.code}/pdf`} target="_blank" rel="noreferrer" className="gp-btn-teal w-full rounded-xl py-3 font-medium mt-3 flex items-center justify-center gap-2">
              <Download size={18} /> Download PDF ticket
            </a>
            {v.terms && <p className="text-xs text-[#8a97a8] mt-5 leading-relaxed"><strong>Terms:</strong> {v.terms}</p>}
          </div>
        </div>

        {v.same_night && v.same_night.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mt-5" data-testid="same-night-shows">
            <p className="font-semibold text-[#16243b] mb-1">Also on the same night 🎉</p>
            <p className="text-sm text-[#8a97a8] mb-3">More from {v.business_name} — why not make a night of it?</p>
            <div className="space-y-2">
              {v.same_night.map((s) => (
                <a key={s.id} href={`/store/${v.business_slug}/events/${s.slug}?promo=samenight`} data-testid={`same-night-${s.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 hover:border-[#2ec9c9] p-3 transition-colors">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#16243b] truncate">{s.title}</p>
                    <p className="text-xs text-[#8a97a8]">{s.event_date ? new Date(s.event_date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}{s.venue_name ? ` · ${s.venue_name}` : ""}{s.price ? ` · ${s.price}` : ""}</p>
                  </div>
                  <span className="gp-btn-teal rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap shrink-0">Get tickets</span>
                </a>
              ))}
            </div>
          </div>
        )}
        <p className="text-center text-[#8a97a8] text-sm mt-6">Powered by TicketsPro.app</p>
      </div>
    </div>
  );
}
